import { QueryHookOptions } from '@apollo/client';

import {
  ActivityLogFilter,
  SiteConnectionHistoryFilter,
  SiteConnectionHistoryFragment,
  SiteConnectionHistoryQuery,
  SiteConnectionHistoryQueryVariables,
  SiteConnectionHistorySummaryFilter,
  SiteConnectionHistorySummaryQuery,
  SiteConnectionHistorySummaryQueryVariables,
  SiteMonitorActivityLogItemFragment,
  SiteMonitorActivityLogQuery,
  SiteMonitorActivityLogQueryVariables,
  useSiteConnectionHistoryQuery,
  useSiteConnectionHistorySummaryQuery,
  useSiteMonitorActivityLogQuery,
} from '@occc/graphql';
import { usePaginatedQuery } from '@occc-app/hooks/usePaginatedQuery';

const transformDataToArray = (
  data: SiteMonitorActivityLogQuery,
): SiteMonitorActivityLogItemFragment[] => {
  const edges = data.siteMonitorActivityLog.edges;
  return edges.map((item) => item.node);
};

const transformConnectionHistoryDataToArray = (
  data: SiteConnectionHistoryQuery,
): SiteConnectionHistoryFragment[] => {
  const edges = data.siteConnectionHistory?.edges || [];
  return edges.map((item) => item.node);
};

export const useGetActivityLog = (
  filter: ActivityLogFilter,
  pageSize?: number,
  baseOptions?: QueryHookOptions<SiteMonitorActivityLogQuery, SiteMonitorActivityLogQueryVariables>,
) => {
  const response = usePaginatedQuery(
    useSiteMonitorActivityLogQuery,
    'siteMonitorActivityLog.pageInfo',
    pageSize,
    {
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      variables: {
        filter,
      },
      ...baseOptions,
    },
  );

  return {
    ...response,
    data: response.data ? transformDataToArray(response.data) : response.data,
  };
};

export const useGetSiteConnectionHistory = (
  filter: SiteConnectionHistoryFilter,
  pageSize?: number,
  baseOptions?: QueryHookOptions<SiteConnectionHistoryQuery, SiteConnectionHistoryQueryVariables>,
) => {
  const response = usePaginatedQuery(
    useSiteConnectionHistoryQuery,
    'siteConnectionHistory.pageInfo',
    pageSize,
    {
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      variables: {
        filter,
      },
      ...baseOptions,
    },
  );

  return {
    ...response,
    data: response.data ? transformConnectionHistoryDataToArray(response.data) : response.data,
  };
};

export const useGetSiteConnectionHistorySummary = (
  filter: SiteConnectionHistorySummaryFilter,
  baseOptions?: QueryHookOptions<
    SiteConnectionHistorySummaryQuery,
    SiteConnectionHistorySummaryQueryVariables
  >,
) => {
  const response = useSiteConnectionHistorySummaryQuery({
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    variables: {
      filter,
    },
    ...baseOptions,
  });

  return {
    ...response,
    data: response.data ? response.data.siteConnectionHistorySummary : response.data,
  };
};
